/**
 * The look `Button`, `IconButton` and `LinkButton` share. One set of style objects, so a link that
 * looks like a button cannot drift from the button beside it.
 *
 * Heights come from the density theme: `lg` is 52px comfortable and 40px compact, `md` 44px and
 * 32px, `sm` 36px and 28px, and on a touch screen a compact control takes its comfortable height
 * back. Pill corners are the default, on the customer site, marketing and the clinic portal as it
 * stands. `rounded` is the admin's, where a control sits in a table or a dense form.
 */
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
export type ButtonVariant = "primary" | "secondary" | "tertiary" | "danger" | "dangerSecondary";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonCorners = "pill" | "rounded";
export const buttonStyle = {
  base: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x1173whf",
    kzqmXN: "xeq5yr9",
    ks0D6T: "x193iq5w",
    kB7OPa: "x9f619",
    kMv6JI: "x1cikjda",
    k63SB2: "xqu410z",
    kLWn49: "x1e5flnj",
    k9WMMc: "x2b8uid",
    kybGjl: "x1hl2dhg",
    kMzoRj: "xdq7b4a",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x9r1u3d",
    kkrTdU: "x1ypdohk",
    k1ekBW: "xv65o4f",
    kIyJzY: "x149xavy",
    kAMwcw: "xa02eh3",
    k3aq6I: "xk4oym4",
    kQizK7: "x1i10hfl",
    $$css: true
  },
  pill: {
    kaIpWk: "x1m3fsnl",
    $$css: true
  },
  rounded: {
    kaIpWk: "x1fbwzx",
    $$css: true
  },
  flush: {
    kZCmMZ: "x1c1uobl",
    $$css: true
  },
  lg: {
    kAzted: "x1nchb1j",
    kg3NbH: "x179o8vp",
    kGuDYH: "x1rf3zrh",
    $$css: true
  },
  md: {
    kAzted: "x1envrbd",
    kg3NbH: "x3i8z2n",
    kGuDYH: "x1awo3zi",
    $$css: true
  },
  sm: {
    kAzted: "xn2nem0 xlivvtk",
    kg3NbH: "xerji9v",
    kGuDYH: "x1awo3zi",
    $$css: true
  },
  lgLoading: {
    kg3NbH: "xj8mq8o",
    kOIVth: "xskm3c9",
    $$css: true
  },
  mdLoading: {
    kg3NbH: "x18xxpcq",
    kOIVth: "xskm3c9",
    $$css: true
  },
  smLoading: {
    kg3NbH: "x1tdra3i",
    kOIVth: "xskm3c9",
    $$css: true
  },
  fullWidth: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  fullWidthPhone: {
    kzqmXN: "x1t1oewe",
    $$css: true
  },
  phoneFloorLg: {
    kAzted: "x1nchb1j x8xiq1h",
    $$css: true
  },
  phoneFloorMd: {
    kAzted: "x1envrbd xulsuzv",
    $$css: true
  },
  primary: {
    kWkggS: "xmbr0q7 xqb7691",
    kMwMTN: "xxil687",
    $$css: true
  },
  secondary: {
    kWkggS: "x40k5rp x14uvmqf xhdl3qd",
    kVAM5u: "x1j7zvbq xpqc6lc",
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  tertiary: {
    kWkggS: "xjbqb8w x14uvmqf xhdl3qd",
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  danger: {
    kWkggS: "xz15occ xukxinq",
    kMwMTN: "xxil687",
    $$css: true
  },
  dangerSecondary: {
    kWkggS: "x40k5rp xioq4ed",
    kVAM5u: "x1j7zvbq",
    kMwMTN: "x6ygkrl",
    $$css: true
  },
  pressed: {
    kWkggS: "x3oldu3",
    kVAM5u: "xtfhg7g",
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  disabled: {
    kWkggS: "x14ba300",
    ksu8eU: "xbsl7fq",
    kVAM5u: "x1j7zvbq",
    kMwMTN: "x16nvyyk",
    kkrTdU: "x1h6gzvc",
    k3aq6I: "x1c071of",
    $$css: true
  },
  loading: {
    kkrTdU: "x19v85br",
    k3aq6I: "x1c071of",
    $$css: true
  },
  slot: {
    k1xSpc: "x3nfvp2",
    kmuXW: "x2lah0s",
    kzqmXN: "x1z0q0p5",
    kZKoxP: "xq9ob8z",
    $$css: true
  },
  spinnerAlone: {
    kzqmXN: "xeh5slm",
    kZKoxP: "x3ycs4o",
    $$css: true
  },
  label: {
    k7Eaqz: "xeuugli",
    $$css: true
  }
};